import * as React from 'react';
import { FC, useState, useCallback, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Form, Alert } from 'reactstrap';
import { GenerateReportTemplateRequest, ReportSettings } from '../../../../models';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import ButtonsContainer from '../../../../components/ButtonsContainer';
import { TextInputField, AutocompleteTextField, NumberInputField } from '../../../../components/Forms/fields';
import { ReportFieldDefinition } from '../../../../models';
import { moveItem } from '../../../../shared/utils';
import ReportFieldModal from './ReportFieldModal';
import ReportFormContent from '../../../../components/Forms/ReportForm/ReportFormContent';
import { ReportFieldEditAction } from '../../../../components/Forms/ReportForm/ReportField';
import { useGeneralSettings, useServicesSet } from '../../../../store/settings/hooks';
import { CheckInputField } from '../../../../components/Forms/fields/CheckInputField';
import { useReactToPrint } from 'react-to-print';
import ReportHeader from '../../../../components/Forms/ReportForm/ReportHeader';
import { serviceId, serviceName } from '../../../../shared/appointmint/appointment';
import { CircularProgress, Collapse, Popover, Tooltip } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PaletteIcon from '@material-ui/icons/Palette';
import EditIcon from '@material-ui/icons/Edit';
import SparkleAIIcon from '../../../../components/Icons/SparkleAIIcon';
import SettingsIcon from '@material-ui/icons/Settings';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useDispatch } from 'react-redux';
import { alertError } from '../../../../store/alerts/actions';
import { getErrorMessage } from '../../../../ajax/errors';
import styled from 'styled-components';
import {
    DOCUMENT_ICONS,
    COLOR_OPTIONS,
    getIconComponent,
    PickerButton,
    PopoverContent,
    PopoverTitle,
    IconGrid,
    IconOptionButton,
    ColorGrid,
    ColorSwatch,
    ClearButton,
} from '../../../../shared/documentIconPicker';
import { generateReportTemplateAsync } from '../../../../ajax/requests';
import { useAIPoweredRequests } from '../../../../store/settings/hooks';
import AppButton from '../../../../components/AppButton';

const NameRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: var(--spacer-S);
`;

const NameFieldWrapper = styled.div`
    flex: 1;
`;

const ModeSelectorContainer = styled.div`
    display: flex;
    gap: var(--spacer-M);
    justify-content: center;
    padding: var(--spacer-M) var(--spacer-S);
`;

interface ModeButtonProps {
    $ai?: boolean;
    id?: string;
    type?: string;
    onClick?: () => void;
    children?: React.ReactNode;
}

const ModeButton = styled.button<ModeButtonProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-XS);
    padding: var(--spacer-M) var(--spacer-L);
    border: var(--border-medium) solid ${p => p.$ai ? 'var(--info-color)' : 'var(--appLigthGrey)'};
    border-radius: 8px;
    background: ${p => p.$ai ? 'rgba(102, 126, 234, 0.06)' : 'white'};
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    color: ${p => p.$ai ? 'var(--info-color)' : 'var(--text-body)'};
    min-width: 160px;
    transition: border-color 0.15s, background 0.15s, color 0.15s;

    &:hover {
        border-color: ${p => p.$ai ? 'var(--info-color)' : 'var(--appBlue)'};
        background: ${p => p.$ai ? 'rgba(102, 126, 234, 0.12)' : 'var(--bg-hover-light)'};
        color: ${p => p.$ai ? 'var(--info-color)' : 'var(--appBlue)'};
    }

    svg {
        font-size: 2rem;
    }
`;

interface AdvancedToggleProps {
    id?: string;
    type?: string;
    onClick?: () => void;
    children?: React.ReactNode;
}

const AdvancedToggle = styled.button<AdvancedToggleProps>`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: var(--spacer-XS) 0;
    font-size: 0.9rem;
    width: 100%;

    &:hover {
        color: var(--text-body);
    }
`;

const AdvancedContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
    padding: var(--spacer-S);
    background: var(--bg-light);
    border-radius: 4px;
    border: var(--border-thin) solid var(--appLigthGrey);
    margin-top: var(--spacer-XS);
`;

const GeneratingOverlay = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-S);
    padding: var(--spacer-L);
    color: var(--info-color);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    background: var(--bg-light);
`;

const GeneratingLabel = styled.div`
    font-size: 0.95rem;
    color: var(--text-muted);
`;

const AISection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const ReportSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const ReportFieldsBox = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    padding: var(--spacer-S);
    border-radius: 4px;
`;

const SectionTitle = styled.h3`
    text-align: center;
    margin: 0;
`;

const SectionActions = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--spacer-S);
`;

export interface ReportConfigurationModalProps {
    defaultValue?: ReportSettings,
    isNew?: boolean,
    initialMode?: 'manual' | 'ai',
    onClose?: () => void,
    onSave: (value: ReportSettings) => void,
    onDelete: (id: string) => void,
    onClone?: (value: ReportSettings) => void,
}

const ReportConfigurationModal : FC<ReportConfigurationModalProps> = ({
    defaultValue,
    isNew,
    initialMode,
    onClose,
    onSave,
    onDelete,
    onClone,
}) => {

    const t = useTranslations();
    const dispatch = useDispatch();

    const allAppointmentTypes = useServicesSet();
    const aiPoweredRequests = useAIPoweredRequests();
    const reportGenerationQuota = aiPoweredRequests?.reports?.totalAvailableQuota ?? 5;

    const [addField, setAddField] = useState<boolean>(false);
    const [editField, setEditField] = useState<number>(-1);
    const [report, setReport] = useState<ReportSettings>({id: '', name: '', version: 0, fields: [], creationDate: '', availableForAppointmentsManager: false, availableForAppointmentTypes: [], mandatoryForAppointmentTypes: [], ...defaultValue, ...(isNew && !defaultValue?.printZoom ? { printZoom: 120 } : {})});
    const [exampleValue, setExampleValue] = useState<Record<string, unknown>>({});
    const [readMode, setReadMode] = useState<boolean>(false);
    const [generationPrompt, setGenerationPrompt] = useState<string>('');
    const [generating, setGenerating] = useState<boolean>(false);
    const [createMode, setCreateMode] = useState<'manual' | 'ai' | null>(null);
    const [aiRefineOpen, setAiRefineOpen] = useState<boolean>(false);
    const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);
    const settings = useGeneralSettings();

    const [iconAnchor, setIconAnchor] = useState<HTMLElement | null>(null);
    const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);

    const modalBodyRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!defaultValue) return;
        setReport({...defaultValue, ...(isNew && !defaultValue.printZoom ? { printZoom: 120 } : {})});
        setExampleValue({...Object.fromEntries(defaultValue.fields.map(f => [f.label, f.defaultValue]))});
        setGenerationPrompt('');
        setAdvancedOpen(false);
        setAiRefineOpen(false);
        // Existing reports go straight to manual mode; new empty reports use initialMode or show selector
        setCreateMode(defaultValue.fields.length === 0 ? (initialMode ?? null) : 'manual');
        const scrollable = modalBodyRef.current?.closest('.modal-body');
        scrollable?.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
            const nameInput = document.getElementById('report-config-name') as HTMLInputElement | null;
            nameInput?.focus();
            nameInput?.select();
        }, 50);
    }, [defaultValue]);

    const updateName = useCallback((name: string) => setReport(prev => ({...prev, name})), []);
    const updateHideHeader = useCallback((hideHeader: boolean) => setReport(prev => ({...prev, hideHeader})), []);
    const updateShowReportInline = useCallback((showReportInline: boolean) => setReport(prev => ({...prev, showReportInline})), []);
    const updatePrintZoom = useCallback((printZoom?: number) => setReport(prev => ({...prev, printZoom})), []);

    const updateAppointmentTypes = useCallback((availableForAppointmentTypes: string[]) => {
        setReport(prev => ({
            ...prev as ReportSettings,
            availableForAppointmentTypes,
            mandatoryForAppointmentTypes: prev.mandatoryForAppointmentTypes.filter(t => availableForAppointmentTypes.includes(t)),
        }));
    }, []);

    const updateAvailableForAppointmentsManager = useCallback((availableForAppointmentsManager: boolean) => {
        setReport(prev => ({...prev as ReportSettings, availableForAppointmentsManager}));
    }, []);

    const updateShareWithClient = useCallback((shareWithClient: boolean) => {
        setReport(prev => ({...prev as ReportSettings, shareWithClient: shareWithClient || undefined}));
    }, []);

    const updateAvailableForAllAppointmentTypes = useCallback((availableForAllAppointmentTypes: boolean) => {
        setReport(prev => ({...prev as ReportSettings, availableForAllAppointmentTypes}));
    }, []);

    const splitMinutes = (minutes: number): [number, number, number] => {
        const days = Math.floor((minutes ?? 0) / 60 / 24);
        const hours = Math.floor((minutes ?? 0) % (60 * 24) / 60);
        const min = (minutes ?? 0) % 60;
        return [days, hours, min];
    };

    const updateEditableAfterCreationDays = useCallback((newDays?: number) => {
        setReport(prev => {
            const [_, hours, minutes] = splitMinutes(prev.editableAfterCreationMinutes ?? 0);
            return {...prev as ReportSettings, editableAfterCreationMinutes: (newDays ?? 0) * 60 * 24 + hours * 60 + minutes};
        });
    }, []);

    const updateEditableAfterCreationHours = useCallback((newHours?: number) => {
        setReport(prev => {
            const [days, _, minutes] = splitMinutes(prev.editableAfterCreationMinutes ?? 0);
            return {...prev as ReportSettings, editableAfterCreationMinutes: days * 60 * 24 + (newHours ?? 0) * 60 + minutes};
        });
    }, []);

    const updateEditableAfterCreationMinutes = useCallback((newMinutes?: number) => {
        setReport(prev => {
            const [days, hours, _] = splitMinutes(prev.editableAfterCreationMinutes ?? 0);
            return {...prev as ReportSettings, editableAfterCreationMinutes: days * 60 * 24 + hours * 60 + (newMinutes ?? 0)};
        });
    }, []);

    const updateIcon = useCallback((icon: string | undefined) => {
        setReport(prev => ({...prev as ReportSettings, icon}));
        setIconAnchor(null);
    }, []);

    const updateColor = useCallback((colorCode: string | undefined) => {
        setReport(prev => ({...prev as ReportSettings, colorCode}));
        setColorAnchor(null);
    }, []);

    const updateGenerationPrompt = useCallback((prompt: string) => {
        setGenerationPrompt(prompt);
    }, []);

    const onGenerateReport = useCallback(async () => {
        if (!generationPrompt.trim() || generating) return;

        setGenerating(true);
        try {
            const result = await generateReportTemplateAsync({
                prompt: generationPrompt,
                template: report,
            } as GenerateReportTemplateRequest);

            setReport(result);
            setExampleValue(Object.fromEntries((result.fields ?? []).map(field => [field.label, field.defaultValue])));
            if (aiRefineOpen) {
                setAiRefineOpen(false);
                setGenerationPrompt('');
            }
        } catch (err) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setGenerating(false);
        }
    }, [aiRefineOpen, dispatch, generationPrompt, generating, report, t]);

    const onOpenAddFieldModal = useCallback(() => setAddField(true), []);
    const onCloseField = useCallback(() => { setAddField(false); setEditField(-1); }, []);

    const onAddNewField = useCallback((newField: ReportFieldDefinition) => {
        setReport(prev => ({...prev, fields: [...(prev?.fields || []), newField]}));
        setExampleValue(prev => ({...prev, [newField.label]: newField.defaultValue}));
        setAddField(false);
    }, []);

    const onSaveField = useCallback((newField: ReportFieldDefinition) => {
        setReport(prev => ({...prev, fields: prev.fields.map((f, i) => i !== editField ? f : newField)}));
        setEditField(-1);
        setExampleValue(prev => ({...prev, [newField.label]: newField.defaultValue}));
    }, [editField]);

    const onFieldAction = useCallback((fieldIndex: number, action: ReportFieldEditAction) => {
        switch(action) {
            case 'delete': setReport(prev => ({...prev, fields: prev.fields.filter((_, i) => i !== fieldIndex)})); break;
            case 'edit': setEditField(fieldIndex); break;
            case 'move-up': fieldIndex > 0 && setReport(prev => ({...prev, fields: moveItem(prev.fields, fieldIndex, fieldIndex-1)})); break;
            case 'move-down': fieldIndex < report.fields.length-1 && setReport(prev => ({...prev, fields: moveItem(prev.fields, fieldIndex, fieldIndex+1)})); break;
            default: break;
        }
    }, [report.fields.length]);

    const componentToPrintRef = React.useRef<HTMLDivElement>(null);
    const printZoomFactor = (report.printZoom ?? 100) / 100;
    const handlePrint = useReactToPrint({
        content: () => componentToPrintRef?.current,
        documentTitle: `[${report?.creationDate?.substr(0, 10)}] ${report?.name} (Test)`,
        pageStyle: printZoomFactor > 1
            ? `@page { margin: 10mm; } body > div { zoom: ${printZoomFactor}; width: ${(100 / printZoomFactor).toFixed(1)}%; }`
            : `@page { margin: 10mm; }`,
    });

    const CurrentIconComponent = getIconComponent(report.icon);
    const currentColor = report.colorCode;
    const isNewReport = createMode === null;
    const showAISection = createMode === 'ai';

    return (
        <Modal size="xl" isOpen={!!defaultValue}>
            <ModalHeader toggle={onClose}>
                {t(TK.reportConfiguration)} — {report?.name || t(TK.new)} (v{report?.version || '1'})
            </ModalHeader>
            <ModalBody>
                <div ref={modalBodyRef} />
                {report &&
                    <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>

                        {/* Name + icon/color pickers */}
                        <NameRow>
                            <NameFieldWrapper>
                                <TextInputField
                                    id={'report-config-name'}
                                    label={t(TK.name)}
                                    value={report.name}
                                    onChange={updateName}
                                />
                            </NameFieldWrapper>

                            <Tooltip title={t(TK.selectColor)}>
                                <PickerButton
                                    id={'report-config-color-button'}
                                    $color={currentColor}
                                    $selected={!!colorAnchor}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => setColorAnchor(e.currentTarget)}
                                    size="small"
                                >
                                    <PaletteIcon style={{ color: currentColor || undefined }} />
                                </PickerButton>
                            </Tooltip>

                            <Tooltip title={t(TK.selectIcon)}>
                                <PickerButton
                                    id={'report-config-icon-button'}
                                    $color={currentColor}
                                    $selected={!!iconAnchor}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => setIconAnchor(e.currentTarget)}
                                    size="small"
                                >
                                    {CurrentIconComponent
                                        ? <CurrentIconComponent style={{ color: currentColor || undefined }} />
                                        : <AssignmentIcon style={{ color: currentColor || 'var(--appLigthGrey)' }} />
                                    }
                                </PickerButton>
                            </Tooltip>
                        </NameRow>

                        {/* Mode selector — shown only for new reports */}
                        {isNewReport && (
                            <ModeSelectorContainer>
                                <ModeButton
                                    id={'report-config-mode-manual'}
                                    type="button"
                                    onClick={() => setCreateMode('manual')}
                                >
                                    <EditIcon />
                                    {t(TK.createManually)}
                                </ModeButton>
                                <ModeButton
                                    id={'report-config-mode-ai'}
                                    type="button"
                                    $ai
                                    onClick={() => setCreateMode('ai')}
                                >
                                    <SparkleAIIcon />
                                    {t(TK.createWithAI)}
                                </ModeButton>
                            </ModeSelectorContainer>
                        )}

                        {/* AI section */}
                        {showAISection && (
                            <AISection>
                                <TextInputField
                                    id={'report-config-generation-prompt'}
                                    label={t(TK.reportGenerationPrompt)}
                                    helpText={t(TK.reportGenerationPromptHelp)}
                                    type="textarea"
                                    value={generationPrompt}
                                    onChange={updateGenerationPrompt}
                                />
                                <Alert color="secondary" style={{ marginBottom: 0 }}>
                                    {t(TK.reportGenerationQuotaInfo, reportGenerationQuota.toString())}
                                </Alert>
                                {generating ? (
                                    <GeneratingOverlay>
                                        <CircularProgress size={48} style={{ color: 'var(--info-color)' }} />
                                        <GeneratingLabel>{t(TK.loading)}…</GeneratingLabel>
                                    </GeneratingOverlay>
                                ) : (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <AppButton
                                            id={'report-config-generate-button'}
                                            type="button"
                                            color="primary"
                                            disabled={!generationPrompt.trim()}
                                            onClick={onGenerateReport}
                                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}
                                        >
                                            <SparkleAIIcon style={{ fontSize: '1.1rem' }} />
                                            {report.fields.length ? t(TK.regenerateReportTemplate) : t(TK.generateReportTemplate)}
                                        </AppButton>
                                    </div>
                                )}
                            </AISection>
                        )}

                        {/* Advanced settings — always shown after mode is chosen */}
                        {!isNewReport && (
                            <>
                                <div>
                                    <AdvancedToggle
                                        id={'report-config-advanced-toggle'}
                                        type="button"
                                        onClick={() => setAdvancedOpen(o => !o)}
                                    >
                                        <SettingsIcon style={{ fontSize: '1rem' }} />
                                        {t(TK.advancedConfigurations)}
                                        {advancedOpen ? <KeyboardArrowUpIcon style={{ fontSize: '1rem', marginLeft: 'auto' }} /> : <KeyboardArrowDownIcon style={{ fontSize: '1rem', marginLeft: 'auto' }} />}
                                    </AdvancedToggle>
                                    <Collapse in={advancedOpen}>
                                        <AdvancedContent>
                                            <div style={{display: 'flex', gap: 'var(--spacer-XS)', alignItems: 'center'}}>
                                                <NumberInputField
                                                    id={'report-config-editable-after-creation-days'}
                                                    label={t(TK.editableFor)}
                                                    value={splitMinutes(report.editableAfterCreationMinutes ?? 0)[0]}
                                                    onChange={updateEditableAfterCreationDays}
                                                />
                                                {t(TK.days).toLocaleLowerCase()}&nbsp;&nbsp;
                                                <NumberInputField
                                                    id={'report-config-editable-after-creation-hours'}
                                                    value={splitMinutes(report.editableAfterCreationMinutes ?? 0)[1]}
                                                    onChange={updateEditableAfterCreationHours}
                                                />
                                                {t(TK.hours).toLocaleLowerCase()}&nbsp;&nbsp;
                                                <NumberInputField
                                                    id={'report-config-editable-after-creation-minutes'}
                                                    value={splitMinutes(report.editableAfterCreationMinutes ?? 0)[2]}
                                                    onChange={updateEditableAfterCreationMinutes}
                                                />
                                                {t(TK.minutes).toLocaleLowerCase()}
                                            </div>

                                            {!report.availableForAllAppointmentTypes && (
                                                <AutocompleteTextField
                                                    id={'report-config-available-for'}
                                                    label={t(TK.availableFor)}
                                                    multiSelect
                                                    options={allAppointmentTypes.map(s => serviceName(s, settings))}
                                                    values={report.availableForAppointmentTypes.map(s => serviceName(s, settings))}
                                                    onValuesChanged={values => updateAppointmentTypes(values.map(v => serviceId(v, settings)))}
                                                />
                                            )}
                                            <CheckInputField
                                                id={'report-config-available-for-all-services'}
                                                label={t(TK.availableForAllServices)}
                                                value={!!report.availableForAllAppointmentTypes}
                                                onChange={updateAvailableForAllAppointmentTypes}
                                            />
                                            <CheckInputField
                                                id={'report-config-available-for-appointments-manager'}
                                                label={t(TK.availableForAdministratives)}
                                                value={!!report.availableForAppointmentsManager}
                                                onChange={updateAvailableForAppointmentsManager}
                                            />
                                            <CheckInputField
                                                id={'report-config-share-with-client'}
                                                label={t(TK.shareWithClient)}
                                                helpText={t(TK.shareWithClientHelp)}
                                                value={!!report.shareWithClient}
                                                onChange={updateShareWithClient}
                                            />
                                            <CheckInputField
                                                id={'report-config-hide-header'}
                                                label={t(TK.hideHeader)}
                                                value={!!report.hideHeader}
                                                onChange={updateHideHeader}
                                            />
                                            <CheckInputField
                                                id={'report-config-show-inline'}
                                                label={t(TK.showReportInline)}
                                                value={!!report.showReportInline}
                                                onChange={updateShowReportInline}
                                            />
                                            <NumberInputField
                                                id={'report-config-print-zoom'}
                                                label={t(TK.printZoom)}
                                                value={report.printZoom ?? 100}
                                                onChange={updatePrintZoom}
                                            />
                                        </AdvancedContent>
                                    </Collapse>
                                </div>

                                <hr style={{width: '100%', margin: 0}}/>

                                <ReportSection>
                                    <SectionTitle>{t(TK.report)}</SectionTitle>
                                    <ReportFieldsBox>
                                        <div ref={componentToPrintRef} style={{marginLeft: 'var(--spacer-XS)', marginRight: 'var(--spacer-XS)'}}>
                                            {readMode && <ReportHeader companyLogo={settings.companyImageUrl} report={exampleValue as any} template={report} />}
                                            <ReportFormContent
                                                id={'report-config-content'}
                                                fields={report.fields || []}
                                                value={exampleValue}
                                                readOnly={readMode}
                                                onChange={(field, value) => {
                                                    setExampleValue(prev => ({...prev, [field]: value}));
                                                    setReport(prev => ({...prev, fields: prev.fields.map(f => f.label === field ? {...f, defaultValue: value} : f)}));
                                                }}
                                                showEditActions
                                                onAction={onFieldAction}
                                            />
                                        </div>
                                    </ReportFieldsBox>
                                    {aiRefineOpen && (
                                        <AISection>
                                            <TextInputField
                                                id={'report-config-refine-prompt'}
                                                label={t(TK.reportGenerationPrompt)}
                                                helpText={t(TK.reportGenerationPromptHelp)}
                                                type="textarea"
                                                value={generationPrompt}
                                                onChange={updateGenerationPrompt}
                                            />
                                            <Alert color="secondary" style={{ marginBottom: 0 }}>
                                                {t(TK.reportGenerationQuotaInfo, reportGenerationQuota.toString())}
                                            </Alert>
                                            {generating ? (
                                                <GeneratingOverlay>
                                                    <CircularProgress size={48} style={{ color: 'var(--info-color)' }} />
                                                    <GeneratingLabel>{t(TK.loading)}…</GeneratingLabel>
                                                </GeneratingOverlay>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <AppButton
                                                        id={'report-config-refine-button'}
                                                        type="button"
                                                        color="primary"
                                                        disabled={!generationPrompt.trim()}
                                                        onClick={onGenerateReport}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}
                                                    >
                                                        <SparkleAIIcon style={{ fontSize: '1.1rem' }} />
                                                        {t(TK.regenerateReportTemplate)}
                                                    </AppButton>
                                                </div>
                                            )}
                                        </AISection>
                                    )}
                                    <SectionActions>
                                        {!readMode && createMode !== 'ai' && report.fields.length > 0 && (
                                            <AppButton
                                                type="button"
                                                color={aiRefineOpen ? 'info' : 'secondary'}
                                                outline={!aiRefineOpen}
                                                onClick={() => setAiRefineOpen(o => !o)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', marginRight: 'auto' }}
                                            >
                                                <SparkleAIIcon style={{ fontSize: '1.1rem' }} />
                                                {t(TK.refineWithAI)}
                                            </AppButton>
                                        )}
                                        <AppButton onClick={() => setReadMode(!readMode)}>{readMode ? t(TK.edit) : t(TK.preview)}</AppButton>
                                        {readMode && <AppButton onClick={handlePrint}>{t(TK.testPrint)}</AppButton>}
                                        {!readMode && <AppButton onClick={onOpenAddFieldModal}>{t(TK.addField)}</AppButton>}
                                    </SectionActions>
                                </ReportSection>
                            </>
                        )}
                    </Form>
                }

                <hr style={{width: '100%'}}/>

                <ButtonsContainer>
                    {!isNew && <AppButton id={'report-config-delete-button'} color="danger" onClick={() => onDelete(report.id)}>{t(TK.delete)}</AppButton>}
                    {!isNew && onClone && <AppButton id={'report-config-clone-button'} color="secondary" onClick={() => onClone(report)}>{t(TK.duplicate)}</AppButton>}
                    {!isNewReport && <AppButton id={'report-config-save-button'} disabled={!report.name?.length} color="success" onClick={() => onSave(report)}>{t(TK.save)}</AppButton>}
                    <AppButton id={'report-config-cancel-button'} onClick={onClose}>{t(TK.cancel)}</AppButton>
                </ButtonsContainer>

                <Popover
                    open={!!colorAnchor}
                    anchorEl={colorAnchor}
                    onClose={() => setColorAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <PopoverContent>
                        <PopoverTitle>{t(TK.selectColor)}</PopoverTitle>
                        <ColorGrid>
                            {COLOR_OPTIONS.map(color => (
                                <ColorSwatch
                                    key={color}
                                    $color={color}
                                    $selected={report.colorCode === color}
                                    onClick={() => updateColor(color)}
                                    title={color}
                                />
                            ))}
                        </ColorGrid>
                        {report.colorCode && (
                            <ClearButton onClick={() => updateColor(undefined)}>✕ clear</ClearButton>
                        )}
                    </PopoverContent>
                </Popover>

                <Popover
                    open={!!iconAnchor}
                    anchorEl={iconAnchor}
                    onClose={() => setIconAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <PopoverContent>
                        <PopoverTitle>{t(TK.selectIcon)}</PopoverTitle>
                        <IconGrid>
                            {DOCUMENT_ICONS.map(({ key, component: IconComp }) => (
                                <IconOptionButton
                                    key={key}
                                    $selected={report.icon === key}
                                    $color={currentColor}
                                    onClick={() => updateIcon(key)}
                                    size="small"
                                >
                                    <IconComp style={{ color: currentColor || undefined }} />
                                </IconOptionButton>
                            ))}
                        </IconGrid>
                        {report.icon && (
                            <ClearButton onClick={() => updateIcon(undefined)}>✕ clear</ClearButton>
                        )}
                    </PopoverContent>
                </Popover>

                <ReportFieldModal
                    id={'report-config-edit-field-new'}
                    isOpen={addField}
                    mode="create"
                    forbiddenNames={report?.fields.map(f => f.label)}
                    onClose={onCloseField}
                    onSave={onAddNewField}
                />

                <ReportFieldModal
                    id={`report-config-edit-field-${editField}`}
                    isOpen={editField >= 0}
                    mode="edit"
                    forbiddenNames={report?.fields.filter((_, i) => i !== editField).map(f => f.label)}
                    defaultValue={report.fields[editField]}
                    onClose={onCloseField}
                    onSave={onSaveField}
                />
            </ModalBody>
        </Modal>
    );
};

export default ReportConfigurationModal;
